/**
 * Blog post: How to Pass the Goethe B2 Exam – Ultimate Practice Tests and Strategy Guide.
 * Content: Schreiben, Sprechen, Lesen & Hören strategy; Redemittel links; Why Logami; CTA.
 */
import React from 'react';
import RelatedResources from './RelatedResources';
import SprechenSamplesCallout from './SprechenSamplesCallout';
import { Link } from 'react-router-dom';
import { useLangParam } from '../../hooks/useLocalizedNavigate';

export default function GoetheB2ExamGuidePost() {
  const lang = useLangParam();
  const blogBase = `/${lang}/blog`;

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        So, you&apos;ve reached the B2 level. Congratulations! You&apos;re no longer just &quot;surviving&quot; in German; you&apos;re starting to live in it. But now, the Goethe-Zertifikat B2 stands between you and your university dreams or professional career in Germany.
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        It&apos;s a high-stakes exam, and let&apos;s be honest: the pressure is real. At logami.app, our team of real German teachers has seen thousands of students struggle with the same thing, fear of the &quot;Blackout.&quot;
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        That&apos;s why we built this practice guideline. We don&apos;t just want you to study; we want you to have a strategy. Here is your roadmap to passing the Goethe B2 exam with confidence.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        1. The Strategy for &quot;Schreiben&quot; (Writing)
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        The Writing module isn&apos;t just about grammar; it&apos;s about structure and <em>Dienstleistung</em> (service to the reader). You have two tasks: a forum post and a formal message.
      </p>
      <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-black/70 leading-relaxed mb-4 ml-2">
        <li><strong>Don&apos;t reinvent the wheel:</strong> The examiners are looking for specific connectors. If you use &quot;und&quot; or &quot;aber&quot; every time, your score will stay low.</li>
        <li><strong>The &quot;Redemittel&quot; Secret:</strong> You need &quot;building blocks.&quot; Instead of saying &quot;Ich finde...&quot;, try &quot;Meiner Auffassung nach...&quot;</li>
        <li><strong>Time Management:</strong> Use our practice materials to simulate the 75-minute window. Writing a great text is easy; writing a great text under pressure is a skill.</li>
      </ul>
      <div className="my-6 p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
        <p className="text-base md:text-lg text-black/80 font-medium mb-2">Crucial Tool:</p>
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-3">
          To master the writing section, you need the right phrases. Check out our deep dive:
        </p>
        <Link
          to={`${blogBase}/goethe-b2-redemittel-schreiben`}
          className="inline-flex items-center font-bold text-black underline underline-offset-2 hover:opacity-80"
        >
          → Essential Redemittel for Goethe B2 Schreiben: Expressing Opinions &amp; Alternatives
        </Link>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        2. The Strategy for &quot;Sprechen&quot; (Speaking)
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        This is where the adrenaline hits. You have to give a short presentation and then engage in a discussion.
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-2">
        <strong>Task 1 (Presentation):</strong> You need a clear Intro, Pro/Contra, and Conclusion.
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        <strong>Task 2 (Discussion):</strong> This isn&apos;t a monologue. You need to react to your partner. Use phrases to agree, disagree, or ask for clarification.
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        <strong>Record Yourself:</strong> Use your phone or the logami.app voice tools to listen back. Do you sound hesitant? Work on your flow with our speaking drills.
      </p>
      <div className="my-6 p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
        <p className="text-base md:text-lg text-black/80 font-medium mb-2">Need the right words?</p>
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-3">
          We&apos;ve compiled the exact phrases you need to sound like a native during the talk:
        </p>
        <Link
          to={`${blogBase}/goethe-b2-redemittel-schreiben`}
          className="inline-flex items-center font-bold text-black underline underline-offset-2 hover:opacity-80"
        >
          → Top Redemittel for the B2 Speaking Exam: How to Discuss and Persuade
        </Link>
      </div>
      <SprechenSamplesCallout provider="goethe" level="b2" />

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        3. Mastering &quot;Lesen&quot; &amp; &quot;Hören&quot; (Reading &amp; Listening)
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        The Goethe texts are &quot;authentic,&quot; meaning they aren&apos;t simplified for students. They are real-world articles and conversations.
      </p>
      <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-black/70 leading-relaxed mb-4 ml-2">
        <li><strong>Keywords are King:</strong> Don&apos;t try to understand every single word. Look for the top keywords in the question and scan the text for synonyms.</li>
        <li><strong>The &quot;Trap&quot; Awareness:</strong> Goethe loves &quot;Distractors.&quot; If you hear the exact word from the question in the audio, be careful, it might be a trick!</li>
        <li><strong>Consistent Practice:</strong> Use our practice tests to get used to the different voices and accents you&apos;ll encounter in the listening section.</li>
      </ul>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Why Logami is Your Secret Weapon
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        We didn&apos;t just build another app. We built an exam simulator that looks and feels exactly like the real Goethe-Zertifikat B2.
      </p>
      <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-black/70 leading-relaxed mb-6 ml-2">
        <li><strong>Real Human Expertise:</strong> Every lesson and strategy is designed by real German teachers who know exactly where students lose points.</li>
        <li><strong>Logami's AI Study Plans:</strong> No more &quot;What should I study today?&quot; Logami's AI analyzes your weaknesses and tells you exactly which materials to focus on.</li>
      </ul>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Final Pro-Tip: Don&apos;t Just Study, Simulate.
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        The biggest reason students fail isn&apos;t a lack of German knowledge, it&apos;s exam anxiety. The best way to kill that anxiety is through repetition.
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-8">
        Ready to see where you stand?
      </p>

      <div className="mt-10 p-6 rounded-2xl border-2 border-black/10 bg-black text-white">
        <p className="text-base md:text-lg text-white/95 font-medium mb-4">
          Start your first full Goethe B2 Mock Exam on logami.app now!
        </p>
        <Link
          to={`/${lang}/dashboard`}
          className="inline-flex items-center justify-center min-h-[48px] px-5 py-3 bg-white text-black font-space font-bold rounded-xl border-2 border-white shadow-md transition-all hover:bg-gray-100 hover:scale-[1.02] active:scale-[0.98]"
        >
          Start Goethe B2 Mock Exam →
        </Link>
      </div>

      <p className="mt-10 pt-6 border-t border-black/10 text-sm text-black/50">
        <strong>Last Updated:</strong> March 2, 2026
      </p>
      <RelatedResources provider="goethe" level="b2" variant="exam-guide" />

    </article>
  );
}
